import React, { useEffect, useState } from 'react';
import {
  MDBBadge,
  MDBBtn,
  MDBIcon,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBSpinner,
  MDBTextArea,
  MDBToast,
} from 'mdb-react-ui-kit';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { duplicateItemPath, getLanguageText } from '../../utils/mediathek';

/** Value of an MDBSelect onChange payload (undefined when the payload is empty). */
export const selectValue = (entry) => (entry && entry.value !== undefined ? entry.value : undefined);

export function StatusBadge({ status }) {
  const { t } = useTranslation();
  const published = status === 'published';
  return (
    <MDBBadge color={published ? 'success' : 'secondary'} light>
      {published ? t('Published') : t('Draft')}
    </MDBBadge>
  );
}

export function TypeBadge({ type }) {
  const { t } = useTranslation();
  if (type === 'podcast') {
    return <MDBBadge color="info" light>{t('Podcast')}</MDBBadge>;
  }
  if (type === 'video') {
    return <MDBBadge color="primary" light>{t('Video')}</MDBBadge>;
  }
  return <MDBBadge color="warning" light>{type || '-'}</MDBBadge>;
}

/**
 * One language of a MultiLangString list (German / English flag + input or textarea).
 */
export function LocalizedInput({ value, language, label, onChange, textarea = false, rows = 3, disabled = false, required = false }) {
  const text = getLanguageText(value, language);
  const countryCode = language === 'en' ? 'gb' : language;
  const fieldLabel = required ? `${label} *` : label;
  return (
    <div className="d-flex align-items-start gap-2 mb-3">
      <ReactCountryFlag countryCode={countryCode} svg style={{ width: '20px', height: '20px', marginTop: '10px' }} />
      <div className="flex-grow-1">
        {textarea ? (
          <MDBTextArea label={fieldLabel} rows={rows} value={text} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        ) : (
          <MDBInput label={fieldLabel} type="text" value={text} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        )}
      </div>
    </div>
  );
}

/** Red alert with one line per (English key) message. */
export function ErrorList({ errors }) {
  const { t } = useTranslation();
  if (!Array.isArray(errors) || errors.length === 0) return null;
  return (
    <div className="alert alert-danger" role="alert">
      {errors.map((error) => (
        <div key={error}>{t(error)}</div>
      ))}
    </div>
  );
}

/**
 * Confirm dialog. Without `onConfirm` it only shows the message with a close
 * button (used to refuse deleting a series that still has items).
 */
export function ConfirmModal({ open, title, message, confirmLabel, onConfirm, onClose, busy = false }) {
  const { t } = useTranslation();
  return (
    <MDBModal open={open} onClose={onClose} staticBackdrop tabIndex="-1">
      <MDBModalDialog centered>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>{title}</MDBModalTitle>
            <MDBBtn className="btn-close" color="none" type="button" onClick={onClose} />
          </MDBModalHeader>
          <MDBModalBody>{message}</MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="secondary" type="button" onClick={onClose} disabled={busy}>
              {onConfirm ? t('Cancel') : t('Close')}
            </MDBBtn>
            {onConfirm && (
              <MDBBtn color="danger" type="button" onClick={onConfirm} disabled={busy}>
                {busy && <MDBSpinner size="sm" className="me-2" />}
                {confirmLabel || t('Delete')}
              </MDBBtn>
            )}
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}

/**
 * Success message passed via navigate(path, { state: { toast } }) - shown once
 * (3 s) and removed from the history entry so a reload does not repeat it.
 */
export function useFlashToast() {
  const location = useLocation();
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);
  const incoming = location.state?.toast;

  useEffect(() => {
    if (!incoming) return;
    setMessage(incoming);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [incoming, location.pathname, location.search, navigate]);

  return [message, setMessage];
}

export function FlashToast({ message, onClose }) {
  const { t } = useTranslation();
  return (
    <MDBToast
      color="success"
      open={Boolean(message)}
      autohide
      delay={3000}
      position="top-right"
      offset={50}
      appendToBody
      onClose={onClose}
      bodyContent={message ? t(message) : ''}
    />
  );
}

/** Font Awesome (solid) icon of a series type: podcast, video, else photo-video. */
export function seriesTypeIcon(type) {
  if (type === 'podcast') return 'podcast';
  if (type === 'video') return 'video';
  return 'photo-video';
}

/**
 * "Duplicate" action of a saved item: opens the series-scoped create page with
 * ?copyFrom=<id> (see duplicateItemPath). `from` (current page path) is passed as
 * router state so Back / save return there. Renders nothing without id / series.
 * `label` (already translated) names the icon variant per row, e.g. "Duplicate: <title>".
 * @param {{item: {id: string, mediaSeriesId: string}, from?: string, variant?: 'outline'|'icon', size?: 'sm'|'lg', className?: string, disabled?: boolean, label?: string}} props
 */
export function DuplicateButton({ item, from, variant = 'outline', size, className = '', disabled = false, label }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const path = duplicateItemPath(item);
  if (!path) return null;
  const handleClick = () => navigate(path, from ? { state: { from } } : undefined);
  if (variant === 'icon') {
    return (
      <MDBBtn
        color="link"
        size="sm"
        type="button"
        className={className}
        aria-label={label || t('Duplicate')}
        title={label || t('Duplicate')}
        onClick={handleClick}
        disabled={disabled}
      >
        <MDBIcon fas icon="copy" aria-hidden="true" />
      </MDBBtn>
    );
  }
  return (
    <MDBBtn color="primary" outline size={size} type="button" className={className} onClick={handleClick} disabled={disabled}>
      <MDBIcon fas icon="copy" className="me-2" aria-hidden="true" />
      {t('Duplicate')}
    </MDBBtn>
  );
}
